// src/components/Visualization/FunnelChart.js
import React, { useState } from 'react';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  Cell,
  LabelList 
} from 'recharts';
import { Filter, ArrowDown } from 'lucide-react';

/**
 * FunnelChart - Visualização de funil para análise de conversão
 * 
 * @param {Object} props
 * @param {Array} props.data - Dados para o funil
 * @param {string} props.nameKey - Chave para os nomes das etapas
 * @param {string} props.valueKey - Chave para os valores de cada etapa
 * @param {boolean} props.showConversionRates - Mostrar taxas de conversão entre etapas
 * @param {boolean} props.showValues - Mostrar valores absolutos
 * @param {boolean} props.showLabels - Mostrar rótulos nas barras
 * @param {boolean} props.horizontal - Orientação horizontal (padrão) ou vertical
 * @param {Function} props.valueFormatter - Formatar valores
 * @param {Function} props.rateFormatter - Formatar taxas de conversão
 * @param {Array} props.colors - Cores personalizadas para as etapas
 * @param {Object} props.comparisonData - Dados de comparação (ex: período anterior)
 * @param {boolean} props.showDropRate - Mostrar taxa de queda (inverso da conversão)
 * @param {number} props.height - Altura do gráfico
 */
const FunnelChart = ({
  data = [],
  nameKey = 'name',
  valueKey = 'value',
  showConversionRates = true,
  showValues = true,
  showLabels = true,
  horizontal = true,
  valueFormatter = value => value.toLocaleString(),
  rateFormatter = rate => `${(rate * 100).toFixed(1)}%`,
  colors = ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE'],
  comparisonData = null,
  showDropRate = false,
  height = 400
}) => {
  // Estados para controle de filtros e visualização
  const [showPercentages, setShowPercentages] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);
  
  // Processar dados e calcular taxas de conversão
  const processedData = React.useMemo(() => {
    if (!data || !data.length) return [];
    
    const result = [...data].map((item, index) => {
      // Calcular taxa de conversão da etapa atual para a próxima
      let conversionRate = 1; // 100% para a primeira etapa
      let dropRate = 0;
      
      if (index < data.length - 1) {
        const currentValue = Number(item[valueKey]) || 0;
        const nextValue = Number(data[index + 1][valueKey]) || 0;
        
        if (currentValue > 0) {
          conversionRate = nextValue / currentValue;
          dropRate = 1 - conversionRate;
        }
      }
      
      // Adicionar taxa em relação ao primeiro valor (funil completo)
      const firstValue = Number(data[0][valueKey]) || 1;
      const currentValue = Number(item[valueKey]) || 0;
      const overallRate = firstValue > 0 ? currentValue / firstValue : 0;
      
      // Formatar valor para exibição
      const formattedValue = valueFormatter(item[valueKey]);
      
      return {
        ...item,
        conversionRate,
        dropRate,
        overallRate,
        formattedValue
      };
    });
    
    return result;
  }, [data, valueKey, valueFormatter]);
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      
      // Encontrar etapa anterior se existir (para referência)
      const itemIndex = processedData.findIndex(d => d[nameKey] === item[nameKey]);
      const prevItem = itemIndex > 0 ? processedData[itemIndex - 1] : null;
      
      return (
        <div className="bg-white p-4 shadow-lg border border-gray-200 rounded-lg min-w-[240px]">
          <h3 className="font-semibold text-gray-800 mb-2">{item[nameKey]}</h3>
          
          <div className="space-y-2">
            {/* Valor absoluto */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total:</span>
              <span className="font-medium">{item.formattedValue}</span>
            </div>
            
            {/* Taxa em relação à etapa anterior */}
            {prevItem && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Conversão da etapa anterior:</span>
                <span className={`font-medium ${item.conversionRate >= 0.5 ? 'text-green-600' : 'text-red-600'}`}>
                  {rateFormatter(item.conversionRate)}
                </span>
              </div>
            )}
            
            {/* Taxa em relação à primeira etapa */}
            {itemIndex > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Conversão total:</span>
                <span className={`font-medium ${item.overallRate >= 0.1 ? 'text-blue-600' : 'text-gray-600'}`}>
                  {rateFormatter(item.overallRate)}
                </span>
              </div>
            )}
            
            {/* Comparação se disponível */}
            {comparisonData && comparisonData[itemIndex] && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Período anterior:</span>
                  <span className="font-medium">
                    {valueFormatter(comparisonData[itemIndex][valueKey])}
                  </span>
                </div>
                
                {/* Variação */}
                {(() => {
                  const currentValue = Number(item[valueKey]) || 0;
                  const previousValue = Number(comparisonData[itemIndex][valueKey]) || 0;
                  if (previousValue === 0) return null;
                  
                  const change = ((currentValue - previousValue) / previousValue) * 100;
                  const isPositive = change >= 0;
                  
                  return (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Variação:</span>
                      <span className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                        {isPositive ? '+' : ''}{change.toFixed(1)}%
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  // Renderizar setas de conversão entre etapas
  const renderConversionArrows = () => {
    if (!showConversionRates || processedData.length <= 1) return null;
    
    return (
      <div className="flex items-center justify-between mb-6 px-4">
        {processedData.slice(0, -1).map((item, index) => {
          const nextItem = processedData[index + 1];
          const conversionRate = nextItem?.conversionRate || 0;
          const isGoodRate = conversionRate >= 0.5;
          
          return (
            <div 
              key={`arrow-${index}`} 
              className="flex items-center text-center"
              style={{ width: `${100 / (processedData.length - 1)}%` }}
            >
              <div className="flex-1" />
              <div className="flex flex-col items-center px-2">
                <ArrowDown 
                  size={20} 
                  className={isGoodRate ? 'text-green-500' : 'text-red-500'} 
                />
                <span 
                  className={`text-xs font-medium ${
                    isGoodRate ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {rateFormatter(conversionRate)}
                </span>
              </div>
              <div className="flex-1" />
            </div>
          );
        })}
      </div>
    );
  };
  
  // Se não tiver dados, mostrar mensagem
  if (!processedData.length) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-gray-500 text-sm">Nenhum dado disponível para exibição.</p>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      {/* Cabeçalho e controles */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Funil de Conversão
        </h3>
        
        <div className="flex items-center space-x-4">
          {/* Alternar entre valores e percentuais */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Exibir:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                className={`px-3 py-1 text-xs rounded-md ${
                  showPercentages ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'
                }`}
                onClick={() => setShowPercentages(true)}
              >
                Percentuais
              </button>
              <button
                className={`px-3 py-1 text-xs rounded-md ${
                  !showPercentages ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'
                }`}
                onClick={() => setShowPercentages(false)}
              >
                Valores
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Setas de conversão entre etapas (somente no modo horizontal) */}
      {horizontal && renderConversionArrows()}
      
      {/* Gráfico do funil */}
      <div style={{ height, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={processedData}
            layout={horizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            barGap={0}
            barCategoryGap="20%"
            onMouseMove={(data) => {
              if (data && data.activeTooltipIndex !== undefined) {
                setActiveIndex(data.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            
            {horizontal ? (
              <>
                <XAxis type="number" hide={showPercentages} />
                <YAxis 
                  dataKey={nameKey} 
                  type="category" 
                  width={150}
                  tick={{ fontSize: 13 }}
                />
              </>
            ) : (
              <>
                <XAxis 
                  dataKey={nameKey} 
                  tick={{ fontSize: 13 }}
                />
                <YAxis type="number" hide={showPercentages} />
              </>
            )}
            
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            <Bar 
              dataKey={valueKey} 
              name={showPercentages ? 'Taxa de Conversão' : 'Valor Absoluto'}
              radius={[4, 4, 4, 4]}
            >
              {processedData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={colors[index % colors.length]}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.6}
                />
              ))}
              
              {showLabels && (
                <LabelList
                  dataKey={showPercentages ? 'overallRate' : valueKey}
                  position={horizontal ? 'right' : 'top'}
                  formatter={(value, entry) => {
                    if (showPercentages) {
                      return rateFormatter(entry.overallRate);
                    }
                    return entry.formattedValue;
                  }}
                  style={{ fill: '#4b5563', fontSize: 12, fontWeight: 500 }}
                />
              )}
            </Bar>
            
            {/* Comparação se disponível */}
            {comparisonData && (
              <Bar 
                dataKey={valueKey} 
                name="Período Anterior" 
                radius={[4, 4, 4, 4]}
                fill="#94a3b8"
                opacity={0.4}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FunnelChart;
